import { isArray } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { isUndefinedOrNull, TPlainObject, trim } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { cloneElement, FC, useContext, useMemo, useRef, useState } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { FormItemClear } from './compts/form-item-clear';
import { FormItemLabel } from './compts/form-item-label';
import { ValidateError } from './compts/validate-error';
import { FormContext } from './context';
import { FieldItemProps, FieldRequiredRule, FieldValue, FormErrorItem } from './types';

/**
 * 说明
 *```
 * 1. 输入类型：input、textarea、virtualinput
 * 2. 参数format 只对输入类型有效
 * 2. 参数removeSpace 只对输入类型有效，枚举值：false、before-after、all
 *   1). 失去焦点时触发
 *   2). before-after（默认值）: 移除前后空格
 *   3). all: 移除所有空格
 *   4). false: 不移除空格
 *```
 */
export const FormItem: FC<FieldItemProps> = (props) => {
  const { form, onValuesChange, formRef, scrollIntoView, showErrorMessage } = useContext(FormContext);
  const {
    registerField,
    setFieldsValueInner,
    getFieldValue,
    getValidateErrorList,
    getFieldsValue,
    setFieldNameList,
    dependenciesFormItemValidate,
  } = form;

  const formItemRef = useRef<HTMLDivElement>(null);
  const forceUpdate = hooks.useForceUpdate();
  const [errorList, setErrorList] = useState<FormErrorItem[]>([]);
  const format = props.format || [];
  const dependencies = props.dependencies || [];

  const suffix = props.suffix;
  const prefix = props.prefix;
  const disabled = props.disabled;
  const readOnly = props.readOnly;

  const childrenTypeName = ((props.children.type['domTypeName'] || '') as string).toLocaleLowerCase();
  const isInput = ['input', 'textarea', 'virtualinput'].includes(childrenTypeName);
  const validateTriggerRef = useRef<'onChange' | 'onBlur' | null>(null);

  /**
   * 获取formItem顺序，用于验证字段顺序
   */
  const fieldSortHandle = hooks.useCallbackRef(() => {
    const nodes = formRef.current?.querySelectorAll('.v-form-item');
    const names = [] as string[];
    if (nodes) {
      nodes.forEach((item) => {
        names.push(item['dataset'].name as string);
      });
    }
    setFieldNameList(names);
  });

  const onScrollIntoView = hooks.useCallbackRef((name: string) => {
    if (scrollIntoView) {
      document.querySelector(`[data-name=${name}]`)?.scrollIntoView();
      props.onScrollIntoView?.();
    }
  });

  const clearValidateMessage = hooks.useCallbackRef(() => {
    props.onValidateResponse?.([]);
    setErrorList([]);
  });

  const changeClearStatus = hooks.useCallbackRef((status) => {
    if (!formItemRef.current) return;
    const clearNode = formItemRef.current.querySelector('.v-form-item-clear') as HTMLElement;
    if (clearNode) {
      clearNode.style.display = status ? 'block' : 'none';
    }
  });

  const formItemValidate = hooks.useCallbackRef((errorList?: FormErrorItem[]) => {
    if (isArray(errorList)) {
      props.onValidateResponse?.(errorList);
      setErrorList(errorList);
    } else {
      const trigger = validateTriggerRef.current;
      const validateTrigger = props.validateTrigger || [];
      if (trigger && !validateTrigger.includes(trigger)) return;
      errorList = getValidateErrorList([props.name]);
      props.onValidateResponse?.(errorList);
      setErrorList(errorList);
    }
  });

  /**
   * value 变化后处理
   * @param name
   * @param value
   * @param isControlled
   */
  const formItemValueChangeHandle = (name: string, value: FieldValue) => {
    setFieldsValueInner({ [name]: value });
    onValueChangeCallback();
  };

  /**
   * 外部通过form.setFieldsValue()赋值处理逻辑
   */
  const onStoreValueChange = hooks.useCallbackRef(() => {
    validateTriggerRef.current = null;
    onValueChangeCallback();
  });

  const onValueChangeCallback = hooks.useCallbackRef(() => {
    const allValues = getFieldsValue();
    const value = allValues[props.name];
    props.onItemValueChange?.(value, allValues);
    onValuesChange?.({ [props.name]: value }, allValues);
    formItemValidate();
    if (dependencies.length > 0) {
      dependencies.forEach((name) => {
        dependenciesFormItemValidate(name);
      });
    }
    forceUpdate();
  });

  const onInputClear = hooks.useCallbackRef(() => {
    validateTriggerRef.current = null;
    formItemValueChangeHandle(props.name, undefined);
    changeClearStatus(false);
    props.onClear?.();
  });

  /**
   * 输入框获取焦点操作
   */
  const onInputFocus = hooks.useCallbackRef((event) => {
    if (readOnly || disabled) return;
    const value = getFieldValue(props.name);
    changeClearStatus(value ? true : false);
    props.children?.props?.onFocus?.(event);
  });

  /**
   * 输入框失去焦点操作
   */
  const onInputBlur = hooks.useCallbackRef((event) => {
    if (readOnly || disabled) return;
    const value = getFieldValue(props.name);
    // 判断是否需要清除空格
    const trimValue = !!props.removeSpace
      ? trim(value as string, props.removeSpace === 'before-after')
      : value;
    if (trimValue !== (value as string)) {
      validateTriggerRef.current = 'onChange';
      formItemValueChangeHandle(props.name, trimValue);
    }
    validateTriggerRef.current = 'onBlur';
    formItemValidate();
    changeClearStatus(false);
    props.children?.props?.onBlur?.(event);
    validateTriggerRef.current = null;
  });

  /**
   * onChange 操作
   */
  const formItemChangeOperate = hooks.useCallbackRef((value: FieldValue, event) => {
    validateTriggerRef.current = isInput ? 'onChange' : null;
    const newValue = value === '' || value === null ? undefined : value;
    formItemValueChangeHandle(props.name, newValue);
    props.children?.props?.onChange?.(newValue, event);
    changeClearStatus(!!newValue);
  });

  // TODO 待研究 不要删除
  // const getChildrenProps = hooks.useCallbackRef(() => {
  //   const controledProps = {
  //     onChange: formItemChangeOperate,
  //     value: getFieldValue(props.name),
  //     key: props.name,
  //     name: props.name,
  //     readOnly,
  //     disabled,
  //   } as Record<string, unknown>;

  //   if (isInput) {
  //     controledProps.onFocus = onInputFocus;
  //     controledProps.onBlur = onInputBlur;
  //     controledProps.format = format;
  //   }
  //   return controledProps;
  // });

  const isRequired = useMemo(() => {
    if (props.rules && props.rules.length > 0) {
      const target = props.rules.find((ruleItem) => (ruleItem as FieldRequiredRule).required === true);
      return target ? true : false;
    }
    return false;
  }, [props.rules]);

  useEffectCustom(() => {
    fieldSortHandle();
    console.log(`Form RegisterField【${props.name}】`);
    const cancelRegister = registerField({
      props,
      onStoreValueChange,
      formItemValidate,
      onScrollIntoView,
      clearValidateMessage,
    });
    return () => {
      console.log(`Form DestroyField【${props.name}】 `);
      if (cancelRegister) cancelRegister();
    };
  }, []);

  const className = classNames(
    'v-form-item',
    `v-form-item-${props.layout}`,
    {
      'v-form-item-value-right': props.valueAlign === 'right',
      'v-form-item-required': isRequired && props.hideRequiredMark != true,
      'v-form-item-error': errorList.length > 0,
      'v-form-item-disabled': disabled,
    },
    props.className,
  );

  const childrenProps: TPlainObject = {
    onChange: formItemChangeOperate,
    value: getFieldValue(props.name),
    key: props.name,
    name: props.name,
    readOnly,
    disabled,
  };

  if (isInput) {
    childrenProps.onFocus = onInputFocus;
    childrenProps.onBlur = onInputBlur;
    childrenProps.format = format;
  } else {
    childrenProps.suffix = suffix;
  }

  if (props.isCustom) {
    return (
      <div
        className={className}
        ref={formItemRef}
        data-name={props.name}
        data-type={childrenTypeName}
        style={props.style}
      >
        {cloneElement(props.children, childrenProps)}
      </div>
    );
  }

  return (
    <div
      className={className}
      ref={formItemRef}
      data-name={props.name}
      data-type={childrenTypeName}
      style={props.style}
    >
      <div className="v-form-item-content">
        <FormItemLabel label={props.label} showTips={props.showTips} onTips={props.onTips} />
        <div className="v-form-item-value">
          {prefix ? <span className="v-form-item-value-prefix">{prefix}</span> : null}
          {/** 此处使用 getChildrenProps 时，函数中无法获取最新的变量，例如disabled，需要研究*/}
          {/* {cloneElement(props.children, getChildrenProps())} */}
          {cloneElement(props.children, childrenProps)}
          {isInput && <FormItemClear onClear={onInputClear} />}
          {isInput && suffix}
        </div>
      </div>
      <ValidateError
        errorList={errorList}
        showErrorMessage={!isUndefinedOrNull(showErrorMessage) ? showErrorMessage : props.showErrorMessage}
      />
    </div>
  );
};

FormItem.defaultProps = {
  validateTrigger: ['onChange'],
  removeSpace: 'before-after',
  showErrorMessage: true,
  layout: 'horizontal',
  valueAlign: 'left',
};
